/**
 * Agent Sessions M1–M5 — Frontend Service
 *
 * Handles REST calls + SSE streaming for agent sessions.
 * M4 adds: roster, model tier PATCH, delegation lookup.
 * M5 adds: artifacts, file upload, promote, provenance, promoted entities.
 */
import { apiFetch } from '@/lib/api';
import { fetchJson, mutate } from './base-roros.service';

export interface AgentSessionDTO {
  id: string;
  orgId: string;
  userId: string;
  actorId: string | null;
  title: string | null;
  provider: string;
  model: string;
  status: string;
  sessionType: string;
  messageCount: number;
  lastActivityAt: string | null;
  totalInputTokens?: number;
  totalOutputTokens?: number;
  totalCostUsd?: string;
  /** M4 — user-set model tier override. null = use actor default. */
  modelTierOverride?: 'standard' | 'complex' | null;
  /** M4 — actor name (set in history list view only) */
  actorName?: string | null;
  createdAt: string;
  updatedAt: string;
}

/** M4 — Roster item returned by GET /api/v1/agent-sessions/roster */
export interface RosterItem {
  actorId: string;
  actorName: string;
  routingDescription: string | null;
  isCoordinator: boolean;
  modelTier: 'standard' | 'complex';
  activeSessions: number;
  domains: string[];
}

/** M4 — Task delegation summary for DelegationCard */
export interface TaskDelegationSummary {
  id: string;
  childActorId: string;
  childActorName: string | null;
  taskDescription: string;
  status: string;
  childSessionId?: string | null;
}

export interface AgentSessionMessageDTO {
  id: string;
  sessionId: string;
  role: string;
  content: string;
  toolName?: string;
  toolInput?: Record<string, unknown>;
  toolResult?: Record<string, unknown>;
  invokedSkillId?: string | null;
  createdAt: string;
}

export interface AssignedSkillDTO {
  name: string;
  displayName: string;
  description: string;
}

export interface CreateSessionPayload {
  actorId: string;
}

// ─── M5 types ─────────────────────────────────────────────────────────────────

/** M5 — Artifact produced by create_artifact tool */
export interface SessionArtifact {
  id: string;
  title: string;
  artifact_type: 'markdown' | 'csv' | string | null;
  version: number;
  created_at: string;
  attachment_id: string;
  original_filename: string | null;
}

/** M5 — Draft returned from promote endpoint */
export interface PromotionDraft {
  title?: string;
  summary?: string;
  description?: string;
  body?: string;
}

/** M5 — Promoted entity summary returned by /promoted-entities */
export interface PromotedEntitySummary {
  id: string;
  entityType: 'goal' | 'outcome' | 'knowledge_doc';
  entityId: string;
  entityTitle: string | null;
  messageId: string | null;
  createdAt: string;
}

export interface SSEChunk {
  type: 'text_delta' | 'tool_start' | 'tool_done' | 'done' | 'error' | 'skill_error' | 'usage';
  delta?: string;
  text?: string;
  toolName?: string;
  toolResult?: string;
  result?: string;
  error?: string;
  message?: string;
  inputTokens?: number;
  outputTokens?: number;
  costUsd?: number;
}

const BASE = '/api/v1/agent-sessions';

export const agentSessionsService = {
  async createSession(payload: CreateSessionPayload): Promise<AgentSessionDTO> {
    return mutate<AgentSessionDTO>(BASE, 'POST', payload);
  },

  async listSessions(search?: string): Promise<AgentSessionDTO[]> {
    // Server envelope is { success, data: AgentSessionDTO[], meta } and
    // fetchJson already unwraps `.data` — the result IS the array.
    // (User-found CRITICAL 2026-06-11: double-unwrap crashed the route.)
    const qs = search ? `?search=${encodeURIComponent(search)}` : '';
    const res = await fetchJson<AgentSessionDTO[]>(`${BASE}${qs}`);
    return res ?? [];
  },

  async getSession(id: string): Promise<{ session: AgentSessionDTO; messages: AgentSessionMessageDTO[] }> {
    return fetchJson(`${BASE}/${id}`);
  },

  async cancelSession(id: string): Promise<AgentSessionDTO> {
    return mutate<AgentSessionDTO>(`${BASE}/${id}/cancel`, 'POST', {});
  },

  async listSkills(sessionId: string): Promise<AssignedSkillDTO[]> {
    // Same double-unwrap class as listSessions — fetchJson already unwraps .data.
    const res = await fetchJson<AssignedSkillDTO[]>(`${BASE}/${sessionId}/skills`);
    return res ?? [];
  },

  /** M4 — AC-AGS-M4-2: get active ai_operator roster for the org. */
  async getRoster(): Promise<RosterItem[]> {
    const res = await fetchJson<RosterItem[]>(`${BASE}/roster`);
    return res ?? [];
  },

  /** M4 — AC-AGS-M4-6: set or clear the model tier override on a session.
   *  AC-CHAT-2: response now includes resolvedModel (the model that will serve the next turn). */
  async patchModelTier(sessionId: string, tier: 'standard' | 'complex' | null): Promise<{ id: string; modelTierOverride: 'standard' | 'complex' | null; resolvedModel: string | null }> {
    return mutate(`${BASE}/${sessionId}/model-tier`, 'PATCH', { tier });
  },

  /** M4 — AC-AGS-M4-5: list sessions with optional admin/filter params.
   *  AC-CHAT-7/8: added `search` for title+message content filtering. */
  async listAllSessions(opts: { all?: boolean; status?: string | null; actorName?: string | null; search?: string | null; limit?: number; offset?: number } = {}): Promise<{ items: AgentSessionDTO[]; total: number }> {
    const params = new URLSearchParams();
    if (opts.all) params.set('all', 'true');
    if (opts.status && opts.status !== 'all') params.set('status', opts.status);
    if (opts.actorName) params.set('actorName', opts.actorName);
    if (opts.search) params.set('search', opts.search);
    if (opts.limit !== undefined) params.set('limit', String(opts.limit));
    if (opts.offset !== undefined) params.set('offset', String(opts.offset));
    const qs = params.toString() ? `?${params.toString()}` : '';
    // fetchJson unwraps .data; for paginated responses we need to call apiFetch directly
    const response = await apiFetch(`${BASE}${qs}`);
    const body = await response.json();
    if (!body.success) throw new Error(body.error?.message ?? 'Failed to list sessions');
    return { items: body.data ?? [], total: body.meta?.total ?? 0 };
  },

  // ─── AC-CHAT-10: rename session title ────────────────────────────────────

  /** AC-CHAT-10 — rename a session title via PATCH /:id/title */
  async renameSession(sessionId: string, title: string): Promise<AgentSessionDTO> {
    return mutate<AgentSessionDTO>(`${BASE}/${sessionId}/title`, 'PATCH', { title });
  },

  // ─── M5 methods ───────────────────────────────────────────────────────────

  /** M5 — AC-M5-3: list generated artifacts for a session. */
  async getArtifacts(sessionId: string): Promise<SessionArtifact[]> {
    const res = await fetchJson<SessionArtifact[]>(`${BASE}/${sessionId}/artifacts`);
    return res ?? [];
  },

  /**
   * M5 — AC-M5-4: link an uploaded attachment to a session as direction='uploaded'.
   * Call after finalize to create the roros_session_files row and enqueue processing.
   */
  async registerUpload(sessionId: string, attachmentId: string): Promise<{ sessionFileId: string }> {
    return mutate(`${BASE}/${sessionId}/uploads`, 'POST', { attachmentId });
  },

  /**
   * M5 — AC-M5-7: promote a message to a goal, outcome, or knowledge doc draft.
   * Returns the draft pre-fill object.
   */
  async promoteMessage(
    sessionId: string,
    messageId: string,
    targetType: 'goal' | 'outcome' | 'knowledge_doc',
  ): Promise<{ draftType: 'goal' | 'outcome' | 'knowledge_doc'; draft: PromotionDraft | null }> {
    return mutate(`${BASE}/${sessionId}/promote`, 'POST', { messageId, targetType });
  },

  /**
   * M5 — AC-M5-7: write provenance row when an entity is saved from a session message.
   * Should be called after the entity is persisted (goal/outcome/knowledge_doc created).
   */
  async writeProvenance(
    sessionId: string,
    entityType: 'goal' | 'outcome' | 'knowledge_doc',
    entityId: string,
    messageId: string | null,
  ): Promise<void> {
    await mutate(`${BASE}/${sessionId}/provenance`, 'POST', { entityType, entityId, messageId });
  },

  /** M5 — AC-M5-8: list entities promoted from a session. */
  async getPromotedEntities(sessionId: string): Promise<PromotedEntitySummary[]> {
    const res = await fetchJson<PromotedEntitySummary[]>(`${BASE}/${sessionId}/promoted-entities`);
    return res ?? [];
  },

  /**
   * Opens an SSE stream for a turn.
   * Yields SSEChunk objects as they arrive.
   * Closes when done/error.
   *
   * AC-AGS-4 reconnect: if the connection drops, the caller can re-fetch
   * the session via getSession() to hydrate from REST.
   */
  async *streamMessage(sessionId: string, message: string): AsyncGenerator<SSEChunk> {
    const res = await apiFetch(`${BASE}/${sessionId}/messages/stream`, {
      method: 'POST',
      body: JSON.stringify({ message }),
    });

    if (!res.ok) {
      const err = await res.json().catch(() => null);
      const msg = err?.error?.message ?? `SSE request failed (${res.status})`;
      yield { type: 'error', error: msg };
      return;
    }

    if (!res.body) {
      yield { type: 'error', error: 'No response body' };
      return;
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const data = line.slice(6).trim();
          if (!data) continue;
          try {
            const chunk = JSON.parse(data) as SSEChunk;
            yield chunk;
            if (chunk.type === 'done' || chunk.type === 'error' || chunk.type === 'skill_error') return;
          } catch {
            // ignore parse errors
          }
        }
      }
    } finally {
      reader.releaseLock();
    }
  },
};
